import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import AppLayout from "@/components/feature/AppLayout";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useWorkspaceRoles } from "@/hooks/useWorkspaceRoles";
import { useWorkspaceDirectory } from "./hooks/useWorkspaceDirectory";
import { formatDirectoryDateTime } from "@/lib/formatDirectoryTime";
import InviteAgentModal from "./components/InviteAgentModal";
import ReinviteAgentButton from "./components/ReinviteAgentButton";
import RevokeAccessButton from "./components/RevokeAccessButton";
import { changeAgentRoles, resetAgentTwoFa, revokeAgentInvitation } from "@/api/directoryApi";
import { useAuthContext } from "@/contexts/AuthContext";
import { useAgentPresence } from "@/contexts/AgentPresenceContext";

/** Pill badge for invitation status. */
function InviteStatusBadge({ status, expiresAt }) {
  const isPending = status === "pending";
  const expiresIn = useMemo(() => {
    if (!isPending || !expiresAt) return null;
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (ms <= 0) return null;
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return h > 0 ? `${h}h` : `${m}m`;
  }, [isPending, expiresAt]);

  if (isPending) {
    return (
      <span
        title={expiresAt ? `Expires ${new Date(expiresAt).toLocaleString()}` : undefined}
        className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:ring-amber-800/60"
      >
        <i className="ri-time-line text-[9px]" />
        Pending{expiresIn ? ` · ${expiresIn}` : ""}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-700 ring-1 ring-rose-200 dark:bg-rose-950/50 dark:text-rose-300 dark:ring-rose-800/60">
      <i className="ri-close-circle-line text-[9px]" />
      Expired
    </span>
  );
}

/** Pill badge for accepted-agent status (active vs revoked). */
function AgentStatusBadge({ isActive }) {
  if (isActive) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:ring-emerald-800/60">
        <i className="ri-checkbox-circle-line text-[9px]" />
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700">
      <i className="ri-forbid-line text-[9px]" />
      Revoked
    </span>
  );
}

function rolesEqual(a, b) {
  const left = [...(a ?? [])].sort();
  const right = [...(b ?? [])].sort();
  return left.length === right.length && left.every((v, i) => v === right[i]);
}

/**
 * Multi-select roles in a dropdown; saves on Apply.
 *
 * `workspaceId` is the workspace whose roles are shown and written. Roles are
 * per-workspace, so it must match the row being edited rather than defaulting to
 * the active workspace.
 */
function RoleDropdown({
  currentRoles,
  agentEmail,
  workspaceId,
  isAdmin,
  roleOptions,
  roleLabels,
  lockedReason,
  onRolesChanged,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [draft, setDraft] = useState(/** @type {string[]} */ ([]));
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(/** @type {{ top: number, left: number, width: number } | null} */ (null));

  useEffect(() => {
    setDraft([...(currentRoles ?? [])]);
  }, [currentRoles]);

  const updateMenuPosition = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const width = 224;
    const rowHeight = 34;
    const footerHeight = 52;
    const menuHeight = Math.min(roleOptions.length * rowHeight + footerHeight + 16, window.innerHeight - 16);
    const gap = 4;
    let top = rect.bottom + gap;
    if (top + menuHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - menuHeight - gap);
    }
    let left = rect.left;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    setMenuStyle({ top, left, width });
  }, [roleOptions.length]);

  useEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return;
    }
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const target = e.target;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
      setDraft([...(currentRoles ?? [])]);
      setErr("");
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, currentRoles]);

  const summary = useMemo(() => {
    const roles = currentRoles ?? [];
    if (!roles.length) return "No roles";
    if (roles.length === 1) return roleLabels[roles[0]] ?? roles[0];
    return `${roles.length} roles`;
  }, [currentRoles, roleLabels]);

  const toggleDraft = useCallback((roleValue, checked) => {
    setDraft((prev) => {
      const set = new Set(prev);
      if (checked) set.add(roleValue);
      else set.delete(roleValue);
      return [...set];
    });
    setErr("");
  }, []);

  const handleApply = useCallback(async () => {
    if (draft.length === 0) {
      setErr("Select at least one role.");
      return;
    }
    if (rolesEqual(draft, currentRoles)) {
      setOpen(false);
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await changeAgentRoles({ email: agentEmail, roles: draft, workspaceId });
      onRolesChanged(agentEmail, draft, workspaceId);
      setOpen(false);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Role change failed.");
    } finally {
      setBusy(false);
    }
  }, [agentEmail, currentRoles, draft, onRolesChanged, workspaceId]);

  if (!isAdmin || lockedReason) {
    const displayRoles = currentRoles?.length ? currentRoles : [];
    return (
      <div className="flex flex-col items-start gap-1">
        <div className="flex flex-wrap gap-1">
          {displayRoles.length ? (
            displayRoles.map((role) => (
              <span
                key={role}
                className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300"
              >
                {roleLabels[role] ?? role}
              </span>
            ))
          ) : (
            <span className="text-[11px] text-slate-400 dark:text-slate-500">—</span>
          )}
        </div>
        {lockedReason ? (
          <span className="text-[10px] font-medium text-slate-400 dark:text-slate-400">
            {lockedReason === "tenant_owner" ? "Tenant owner · always admin" : "Role locked"}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy || roleOptions.length === 0}
        className="flex min-w-[8.5rem] max-w-[14rem] items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-left text-xs font-medium text-slate-700 shadow-sm transition hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-slate-500 dark:hover:bg-slate-700/80"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate">{busy ? "Saving…" : summary}</span>
        <i className={`ri-arrow-${open ? "up" : "down"}-s-line shrink-0 text-sm text-slate-400 dark:text-slate-500`} />
      </button>

      {open && menuStyle
        ? createPortal(
            <div
              ref={menuRef}
              className="fixed z-[200] rounded-lg border border-slate-200 bg-white py-2 shadow-lg ring-1 ring-slate-200/60 dark:border-slate-600 dark:bg-slate-900 dark:ring-slate-700/60"
              style={{ top: menuStyle.top, left: menuStyle.left, width: menuStyle.width }}
              role="listbox"
            >
              <div className="space-y-0.5 px-2">
                {roleOptions.map((opt) => (
                  <label
                    key={opt.value}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    <input
                      type="checkbox"
                      checked={draft.includes(opt.value)}
                      onChange={(e) => toggleDraft(opt.value, e.target.checked)}
                      className="h-3.5 w-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
              <div className="mt-2 flex items-center justify-end gap-2 border-t border-slate-100 px-2 pt-2">
                <button
                  type="button"
                  className="rounded px-2 py-1 text-[11px] font-medium text-slate-500 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                  onClick={() => {
                    setOpen(false);
                    setDraft([...(currentRoles ?? [])]);
                    setErr("");
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={busy}
                  className="rounded bg-teal-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-teal-700 disabled:opacity-60"
                  onClick={() => void handleApply()}
                >
                  Apply
                </button>
              </div>
            </div>,
            document.body
          )
        : null}

      {err ? <p className="mt-0.5 max-w-[14rem] text-[10px] text-rose-600 dark:text-rose-400">{err}</p> : null}
    </div>
  );
}

/**
 * Roles cell for an accepted agent.
 *
 * An agent can belong to several workspaces with a different role set in each. The
 * tenant-wide listing therefore renders one editor per workspace so each row edits
 * the roles it displays; the per-workspace listing edits the active workspace only.
 */
function AgentRolesCell({
  agent,
  activeWorkspaceId,
  workspaceNames,
  tenantWide,
  isAdmin,
  roleOptions,
  roleLabels,
  onRolesChanged,
}) {
  const editors = useMemo(() => {
    if (!tenantWide) {
      return [
        {
          workspaceId: activeWorkspaceId,
          roles: agent.roles ?? (agent.role ? [agent.role] : []),
        },
      ];
    }
    const byWorkspace = agent.rolesByWorkspace ?? {};
    const ids = Object.keys(byWorkspace).length
      ? Object.keys(byWorkspace)
      : Array.isArray(agent.agentWorkspaceIds) && agent.agentWorkspaceIds.length
        ? agent.agentWorkspaceIds
        : [activeWorkspaceId];
    return ids
      .map((id) => ({ workspaceId: id, roles: byWorkspace[id] ?? [] }))
      .sort((a, b) => {
        // Active workspace first, then by display name for a stable order.
        if (a.workspaceId === activeWorkspaceId) return -1;
        if (b.workspaceId === activeWorkspaceId) return 1;
        return String(workspaceNames[a.workspaceId] ?? a.workspaceId).localeCompare(
          String(workspaceNames[b.workspaceId] ?? b.workspaceId)
        );
      });
  }, [agent, activeWorkspaceId, workspaceNames, tenantWide]);

  if (editors.length === 1) {
    return (
      <RoleDropdown
        currentRoles={editors[0].roles}
        agentEmail={agent.email}
        workspaceId={editors[0].workspaceId}
        isAdmin={isAdmin}
        roleOptions={roleOptions}
        roleLabels={roleLabels}
        lockedReason={agent.roleLockedReason}
        onRolesChanged={onRolesChanged}
      />
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {editors.map((editor) => (
        <div key={editor.workspaceId} className="flex flex-col gap-0.5">
          <span
            className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500"
            title={editor.workspaceId}
          >
            {workspaceNames[editor.workspaceId] ?? `Workspace ${String(editor.workspaceId).slice(0, 8)}…`}
          </span>
          <RoleDropdown
            currentRoles={editor.roles}
            agentEmail={agent.email}
            workspaceId={editor.workspaceId}
            isAdmin={isAdmin}
            roleOptions={roleOptions}
            roleLabels={roleLabels}
            lockedReason={agent.roleLockedReason}
            onRolesChanged={onRolesChanged}
          />
        </div>
      ))}
    </div>
  );
}

/** Reset 2FA button with inline confirmation. */
function ResetTwoFaButton({ agentEmail }) {
  const [phase, setPhase] = useState(/** @type {"idle"|"confirm"|"loading"|"done"|"error"} */ ("idle"));
  const [err, setErr] = useState("");

  const handleClick = useCallback(async () => {
    if (phase === "idle") {
      setPhase("confirm");
      return;
    }
    if (phase === "confirm") {
      setPhase("loading");
      setErr("");
      try {
        await resetAgentTwoFa({ email: agentEmail });
        setPhase("done");
        setTimeout(() => setPhase("idle"), 3000);
      } catch (ex) {
        setErr(ex instanceof Error ? ex.message : "Reset failed.");
        setPhase("error");
      }
    }
  }, [phase, agentEmail]);

  if (phase === "done") {
    return (
      <span className="text-[11px] font-medium text-emerald-700 dark:text-emerald-400">
        <i className="ri-checkbox-circle-line mr-0.5" /> 2FA removed
      </span>
    );
  }

  if (phase === "error") {
    return (
      <span className="text-[11px] text-rose-600 dark:text-rose-400" title={err}>
        <i className="ri-error-warning-line mr-0.5" /> Failed
      </span>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={handleClick}
        disabled={phase === "loading"}
        className={`rounded px-2 py-1 text-[11px] font-medium transition ${
          phase === "confirm"
            ? "bg-rose-600 text-white hover:bg-rose-700"
            : "text-slate-600 hover:bg-slate-100 hover:text-slate-800 dark:text-slate-300 dark:hover:bg-slate-700/90 dark:hover:text-slate-100"
        } disabled:opacity-60`}
      >
        {phase === "loading" ? (
          <i className="ri-loader-4-line animate-spin" />
        ) : phase === "confirm" ? (
          "Confirm reset"
        ) : (
          "Reset 2FA"
        )}
      </button>
      {phase === "confirm" ? (
        <button
          type="button"
          onClick={() => setPhase("idle")}
          className="text-[11px] text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          Cancel
        </button>
      ) : null}
    </div>
  );
}

/** Revoke invitation button with inline confirmation. */
function RevokeInviteButton({ email, onRevoked, scope = "workspace" }) {
  const [phase, setPhase] = useState(/** @type {"idle"|"confirm"|"loading"} */ ("idle"));
  const [err, setErr] = useState("");
  const tenantWide = scope === "tenant";

  const handleClick = useCallback(async () => {
    if (phase === "idle") {
      setPhase("confirm");
      setErr("");
      return;
    }
    setPhase("loading");
    setErr("");
    try {
      await revokeAgentInvitation({ email, scope });
      await onRevoked();
      setPhase("idle");
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Revoke failed.");
      setPhase("idle");
    }
  }, [phase, email, onRevoked, scope]);

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={handleClick}
          disabled={phase === "loading"}
          className={`rounded px-2 py-1 text-[11px] font-medium transition ${
            phase === "confirm"
              ? "bg-rose-600 text-white hover:bg-rose-700"
              : "text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:text-rose-400 dark:hover:bg-rose-950/50 dark:hover:text-rose-300"
          } disabled:opacity-60`}
        >
          {phase === "loading" ? (
            <i className="ri-loader-4-line animate-spin" />
          ) : phase === "confirm" ? (
            tenantWide ? "Confirm revoke all" : "Confirm revoke"
          ) : tenantWide ? (
            "Revoke all invites"
          ) : (
            "Revoke"
          )}
        </button>
        {phase === "confirm" ? (
          <button
            type="button"
            onClick={() => {
              setPhase("idle");
              setErr("");
            }}
            className="text-[11px] text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            Cancel
          </button>
        ) : null}
      </div>
      {phase === "confirm" && tenantWide ? (
        <p className="max-w-[14rem] text-right text-[10px] leading-snug text-slate-500 dark:text-slate-400">
          Deletes pending invites for this email across every workspace.
        </p>
      ) : null}
      {err ? (
        <p className="max-w-[12rem] text-right text-[10px] leading-snug text-rose-600 dark:text-rose-400" role="alert">
          {err}
        </p>
      ) : null}
    </div>
  );
}

export default function UsersPage() {
  const { workspaces, activeWorkspace, activeWorkspaceId, loading: workspaceLoading } = useWorkspace();
  const { roles: workspaceRoles, isAdmin, loading: rolesLoading } = useWorkspaceRoles();
  const { rows, setRows, total, loading, error, inviteError, roleError, roleOptions, refresh, tenantWide, directoryScope } =
    useWorkspaceDirectory();
  const { user } = useAuthContext();
  const { status: presenceStatus } = useAgentPresence();
  const [search, setSearch] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);

  const canManageWorkspaceUsers = isAdmin || (!rolesLoading && workspaceRoles.length === 0);

  const roleLabels = useMemo(
    () => Object.fromEntries(roleOptions.map((role) => [role.value, role.label])),
    [roleOptions]
  );

  const workspaceNames = useMemo(
    () => Object.fromEntries((workspaces ?? []).map((w) => [String(w.id), w.name])),
    [workspaces]
  );

  const workspaceEyebrow = useMemo(() => {
    if (workspaceLoading) return "Loading workspace…";
    const name = activeWorkspace?.name?.trim();
    if (name) return name;
    if (activeWorkspaceId) return activeWorkspaceId;
    return "Workspace";
  }, [workspaceLoading, activeWorkspace, activeWorkspaceId]);

  const filtered = useMemo(() => {
    const mine = String(user?.email || "").trim().toLowerCase();
    const accepting = presenceStatus === "available";
    const withLivePresence = mine
      ? rows.map((r) =>
          r._type === "agent" && String(r.email || "").trim().toLowerCase() === mine
            ? { ...r, isOnline: accepting }
            : r,
        )
      : rows;
    const q = search.trim().toLowerCase();
    if (!q) return withLivePresence;
    return withLivePresence.filter((r) => {
      const name = String(r.agentName ?? "").toLowerCase();
      const em = String(r.email ?? "").toLowerCase();
      return name.includes(q) || em.includes(q);
    });
  }, [rows, search, user?.email, presenceStatus]);

  /**
   * Optimistically update roles in the local list after a successful role change.
   * Only the edited workspace's roles change; the agent's other workspaces keep theirs.
   */
  const handleRolesChanged = useCallback((email, newRoles, workspaceId) => {
    const editedWorkspaceId = workspaceId || activeWorkspaceId;
    setRows?.((prev) =>
      prev.map((r) => {
        if (r._type !== "agent" || r.email !== email) return r;
        const byWorkspace = { ...(r.rolesByWorkspace ?? {}), [editedWorkspaceId]: newRoles };
        const isActiveWorkspace = editedWorkspaceId === activeWorkspaceId;
        return {
          ...r,
          rolesByWorkspace: byWorkspace,
          roles: isActiveWorkspace ? newRoles : r.roles,
          role: isActiveWorkspace ? newRoles[0] ?? null : r.role,
        };
      })
    );
  }, [setRows, activeWorkspaceId]);

  /** Refetch directory after a successful invitation revoke. */
  const handleInviteRevoked = useCallback(async () => {
    await refresh();
  }, [refresh]);

  const handleAccessRevoked = useCallback(async () => {
    await refresh();
  }, [refresh]);

  return (
    <AppLayout>
      <div className="scrollbar-thin flex h-full min-h-0 flex-col overflow-y-auto bg-app-canvas">
        <header className="shrink-0 border-b border-slate-200/60 bg-white/70 px-5 py-5 backdrop-blur-md sm:px-8 dark:border-slate-700/60 dark:bg-slate-900/70">
          <p className="text-xs font-semibold tracking-wide text-teal-700 dark:text-teal-400" title={activeWorkspaceId ?? undefined}>
            {workspaceEyebrow}
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-50">Support Agents</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
            {tenantWide
              ? `All support agents invited across this tenant (${total} total). Revoke removes access from every enrolled workspace.`
              : `Support agents for this workspace (${total} total), including revoked accounts. Data comes from your tenant Postgres.`}
          </p>
        </header>

        <div className="flex flex-col gap-4 px-5 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-8 sm:py-6">
          <div className="relative max-w-md flex-1">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-slate-400">
              <i className="ri-search-line text-sm" />
            </span>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or email…"
              className="ui-input !pl-10"
              aria-label="Search agents"
            />
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              className="text-xs font-semibold text-teal-700 underline-offset-2 hover:underline dark:text-teal-400"
              onClick={() => void refresh()}
            >
              Refresh
            </button>
            {canManageWorkspaceUsers ? (
              <button
                type="button"
                onClick={() => setInviteOpen(true)}
                className="flex items-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700"
              >
                <i className="ri-user-add-line text-sm" />
                Invite agent
              </button>
            ) : null}
          </div>
        </div>

        {error ? (
          <div className="mx-5 mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900 sm:mx-8">
            {error.message}
          </div>
        ) : null}

        {inviteError ? (
          <div className="mx-5 mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 sm:mx-8">
            Could not load pending invitations: {inviteError.message}
          </div>
        ) : null}

        {roleError ? (
          <div className="mx-5 mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 sm:mx-8">
            Could not load workspace roles: {roleError.message}
          </div>
        ) : null}

        <div className="mx-5 mb-8 sm:mx-8">
          <div className="ui-card-static overflow-hidden !p-0">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-left">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/80 dark:border-slate-700/60 dark:bg-slate-800/50">
                    {["Agent", "Email", "Account", "Roles", "Presence", "Last Login", "Actions"].map((h) => (
                      <th
                        key={h}
                        className="whitespace-nowrap px-5 py-3.5 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-400"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
                  {loading && !rows.length ? (
                    <tr>
                      <td colSpan={7} className="px-5 py-12 text-center text-sm text-slate-500 dark:text-slate-400">
                        Loading agents…
                      </td>
                    </tr>
                  ) : filtered.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-5 py-12 text-center text-sm text-slate-500 dark:text-slate-400">
                        {tenantWide ? "No agents invited across this tenant." : "No agents for this workspace."}
                      </td>
                    </tr>
                  ) : (
                    filtered.map((a, idx) =>
                      a._type === "pending" ? (
                        // ---------- Pending / expired invitation row ----------
                        <tr key={`invite-${a.email}-${a.workspaceId ?? ""}-${idx}`} className="bg-slate-50/40 dark:bg-slate-800/30">
                          <td className="px-5 py-3.5">
                            <span className="text-xs italic text-slate-400 dark:text-slate-500">—</span>
                          </td>
                          <td className="px-5 py-3.5 text-sm text-slate-600 dark:text-slate-300">
                            <div>{a.email}</div>
                            {tenantWide && a.workspaceId ? (
                              <p className="mt-0.5 font-mono text-[10px] text-slate-400 dark:text-slate-500">
                                Workspace {String(a.workspaceId).slice(0, 8)}…
                              </p>
                            ) : null}
                          </td>
                          <td className="px-5 py-3.5">
                            <InviteStatusBadge status={a.status} expiresAt={a.expiresAt} />
                          </td>
                          <td className="px-5 py-3.5">
                            <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                              {roleLabels[a.role] ?? a.role ?? "—"}
                            </span>
                          </td>
                          <td className="px-5 py-3.5">
                            <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
                          </td>
                          <td className="px-5 py-3.5 text-xs text-slate-400 dark:text-slate-400">—</td>
                          <td className="px-5 py-3.5">
                            {canManageWorkspaceUsers ? (
                              <div className="flex flex-col items-end gap-2">
                                {a.status === "expired" ? (
                                  <ReinviteAgentButton
                                    email={a.email}
                                    role={a.role || "chat"}
                                    workspaceId={a.workspaceId || activeWorkspaceId}
                                    onDone={handleInviteRevoked}
                                  />
                                ) : null}
                                <RevokeInviteButton
                                  email={a.email}
                                  scope={directoryScope}
                                  onRevoked={handleInviteRevoked}
                                />
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
                            )}
                          </td>
                        </tr>
                      ) : (
                        // ---------- Accepted agent row (active or revoked history) ----------
                        <tr
                          key={a.id ?? a.email}
                          aria-disabled={!a.isActive}
                          className={
                            a.isActive
                              ? "group transition-colors hover:bg-slate-50/80 dark:hover:bg-slate-800/70"
                              : "bg-slate-50/50 dark:bg-slate-800/40"
                          }
                        >
                          <td className="px-5 py-3.5">
                            <p
                              className={`text-sm font-semibold ${
                                a.isActive
                                  ? "text-slate-900 dark:text-slate-100"
                                  : "text-slate-500 dark:text-slate-400"
                              }`}
                            >
                              {a.agentName}
                            </p>
                          </td>
                          <td
                            className={`px-5 py-3.5 text-sm ${
                              a.isActive
                                ? "text-slate-600 dark:text-slate-300"
                                : "text-slate-400 dark:text-slate-500"
                            }`}
                          >
                            <div>{a.email}</div>
                            {tenantWide && Array.isArray(a.agentWorkspaceIds) && a.agentWorkspaceIds.length > 1 ? (
                              <p className="mt-0.5 text-[10px] text-slate-400 dark:text-slate-500">
                                {a.agentWorkspaceIds.length} workspaces
                              </p>
                            ) : null}
                          </td>
                          <td className="px-5 py-3.5">
                            <AgentStatusBadge isActive={Boolean(a.isActive)} />
                          </td>
                          <td className="px-5 py-3.5">
                            {a.isActive ? (
                              <AgentRolesCell
                                agent={a}
                                activeWorkspaceId={activeWorkspaceId}
                                workspaceNames={workspaceNames}
                                tenantWide={tenantWide}
                                isAdmin={canManageWorkspaceUsers}
                                roleOptions={roleOptions}
                                roleLabels={roleLabels}
                                onRolesChanged={handleRolesChanged}
                              />
                            ) : (
                              <span className="text-[11px] text-slate-400 dark:text-slate-500">—</span>
                            )}
                          </td>
                          <td className="px-5 py-3.5">
                            {a.isActive ? (
                              <div className="flex flex-col gap-0.5">
                                <span
                                  className={`inline-flex items-center gap-1.5 text-xs font-semibold ${
                                    a.isOnline
                                      ? "text-emerald-700 dark:text-emerald-400"
                                      : "text-slate-500 dark:text-slate-400"
                                  }`}
                                >
                                  <span
                                    className={`h-1.5 w-1.5 rounded-full ${
                                      a.isOnline ? "bg-emerald-500" : "bg-slate-400"
                                    }`}
                                    aria-hidden
                                  />
                                  {a.isOnline ? "Online" : "Offline"}
                                </span>
                                <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">
                                  {a.isOnline ? "Active — accepting chats" : "Inactive — not accepting"}
                                </span>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
                            )}
                          </td>
                          <td className="px-5 py-3.5 text-xs text-slate-500 tabular-nums dark:text-slate-300">
                            {a.isActive ? formatDirectoryDateTime(a.lastLogin) : "—"}
                          </td>
                          <td className="px-5 py-3.5">
                            {a.isActive && canManageWorkspaceUsers ? (
                              <div className="flex flex-col items-end gap-2">
                                <ResetTwoFaButton agentEmail={a.email} />
                                {!a.roleLockedReason ? (
                                  <RevokeAccessButton
                                    agentEmail={a.email}
                                    workspaceName={activeWorkspace?.name}
                                    tenantWideOnly={tenantWide}
                                    onRevoked={handleAccessRevoked}
                                  />
                                ) : null}
                              </div>
                            ) : !a.isActive && canManageWorkspaceUsers ? (
                              <ReinviteAgentButton
                                email={a.email}
                                role={a.role || "chat"}
                                workspaceId={a.workspaceId || a.agentWorkspaceIds?.[0] || activeWorkspaceId}
                                onDone={handleAccessRevoked}
                              />
                            ) : (
                              <span className="text-xs text-slate-400 dark:text-slate-500">
                                {a.isActive ? "—" : "Access revoked"}
                              </span>
                            )}
                          </td>
                        </tr>
                      )
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <InviteAgentModal
        open={inviteOpen}
        onClose={() => {
          setInviteOpen(false);
          void refresh();
        }}
        workspaceName={activeWorkspace?.name}
        roleOptions={roleOptions}
      />
    </AppLayout>
  );
}
